import { ApolloQueryResult, FetchResult, gql } from '@apollo/client';
import { apolloMutation, apolloQuery } from '../apollo';
import {
  ApplyInput,
  ApplyMutation,
  AuthorizeCardInput,
  AuthorizeCardMutation,
  CreateDraftAppInput,
  CreateDraftAppMutation,
  CustomerQuery,
  CustomerSearchInput,
  GenerateLeaseContractInput,
  GenerateLeaseContractMutation,
  GetAdditionalCustomerInfoQuery,
  RemoveVirtualCardInput,
  RemoveVirtualCardMutation,
  AcknowledgeLeaseTermsInput,
  AcknowledgeLeaseTermsMutation,
  SelectLeaseOptionInput,
  SelectLeaseOptionMutation,
  SignLeaseAgreementInput,
  SignLeaseAgreementMutation,
  UpdateBankAccountInput,
  UpdateBankAccountMutation,
  UpdateBasicInfoContinuedInput,
  UpdateBasicInfoContinuedMutation,
  UpdateDebitInfoInput,
  UpdateDebitInfoMutation,
  UpdateEmploymentInput,
  UpdateEmploymentMutation,
  UpdateShippingAddressInput,
  UpdateShippingAddressMutation,
  VerifyEmailQuery,
  GetLeaseApplicationItemsQuery,
  GetExistingLeaseDocumentsQuery,
  GetProtectionPlusEnrollmentUrlInput,
  GetProtectionPlusEnrollmentUrlMutation,
} from '../gql/graphql';

export interface ApplicationForm {
  apply(input: ApplyInput): Promise<FetchResult<ApplyMutation>>;
  updateShippingAddress(input: UpdateShippingAddressInput): Promise<FetchResult<UpdateShippingAddressMutation>>;
  generateContract(input: GenerateLeaseContractInput): Promise<FetchResult<GenerateLeaseContractMutation>>;
  updateBasicInfoContinued(input: UpdateBasicInfoContinuedInput): Promise<FetchResult<UpdateBasicInfoContinuedMutation>>;
  updateEmployment(input: UpdateEmploymentInput): Promise<FetchResult<UpdateEmploymentMutation>>;
  authorizeCard(input: AuthorizeCardInput): Promise<FetchResult<AuthorizeCardMutation>>;
  updateDebitInfo(input: UpdateDebitInfoInput): Promise<FetchResult<UpdateDebitInfoMutation>>;
  updateBankAccount(input: UpdateBankAccountInput): Promise<FetchResult<UpdateBankAccountMutation>>;
  signLeaseAgreement(input: SignLeaseAgreementInput): Promise<FetchResult<SignLeaseAgreementMutation>>;
  customer(input: CustomerSearchInput): Promise<ApolloQueryResult<CustomerQuery>>;
  verifyEmail(email: string): Promise<FetchResult<VerifyEmailQuery>>;
  removeVirtualCard(input: RemoveVirtualCardInput): Promise<FetchResult<RemoveVirtualCardMutation>>;
  acknowledgeLeaseOptions(input: AcknowledgeLeaseTermsInput): Promise<FetchResult<AcknowledgeLeaseTermsMutation>>;
  selectLeaseOption(input: SelectLeaseOptionInput): Promise<FetchResult<SelectLeaseOptionMutation>>;
  createDraftApp(input: CreateDraftAppInput): Promise<FetchResult<CreateDraftAppMutation>>;
  getAdditionalCustomerInfo(applicationId: number): Promise<ApolloQueryResult<GetAdditionalCustomerInfoQuery>>;
  getApplicationItems(applicationId: number): Promise<ApolloQueryResult<GetLeaseApplicationItemsQuery>>;
  getExistingLeaseDocuments(orderId: string): Promise<ApolloQueryResult<GetExistingLeaseDocumentsQuery>>;
  getProtectionPlusEnrollmentUrl(input: GetProtectionPlusEnrollmentUrlInput): Promise<FetchResult<GetProtectionPlusEnrollmentUrlMutation>>;
}

const applicationFormService: ApplicationForm = {
  async apply(input: ApplyInput) {
    return apolloMutation(apply, { input: input }) as Promise<FetchResult<ApplyMutation>>;
  },
  async updateShippingAddress(input: any) {
    return apolloMutation(UPDATE_SHIPPING_ADDRESS, { input: input }) as Promise<FetchResult<UpdateShippingAddressMutation>>;
  },
  async generateContract(input: GenerateLeaseContractInput) {
    return apolloMutation(generateLeaseContract, { input: input }) as Promise<FetchResult<GenerateLeaseContractMutation>>;
  },
  async updateBasicInfoContinued(input: UpdateBasicInfoContinuedInput) {
    return apolloMutation(UPDATE_BASIC_INFO_CONTINUED, { input: input }) as Promise<FetchResult<UpdateBasicInfoContinuedMutation>>;
  },
  async updateEmployment(input: UpdateEmploymentInput) {
    return apolloMutation(updateEmployment, { input: input }) as Promise<FetchResult<UpdateEmploymentMutation>>;
  },
  async updateDebitInfo(input: UpdateDebitInfoInput) {
    return apolloMutation(UPDATE_DEBIT_DETAILS, { input: input }) as Promise<FetchResult<UpdateDebitInfoMutation>>;
  },
  async authorizeCard(input: AuthorizeCardInput) {
    return apolloMutation(AUTHORIZE_CARD, { input: input }) as Promise<ApolloQueryResult<AuthorizeCardMutation>>;
  },
  async updateBankAccount(input: UpdateBankAccountInput) {
    return apolloMutation(UPDATE_BANK_ACCOUNT, { input: input }) as Promise<FetchResult<UpdateBankAccountMutation>>;
  },
  async signLeaseAgreement(input: SignLeaseAgreementInput) {
    return apolloMutation(SIGN_LEASE_AGREEMENT, { input: input }) as Promise<FetchResult<SignLeaseAgreementMutation>>;
  },
  async customer(input: CustomerSearchInput) {
    return apolloQuery(customerSearch, { input: input }) as Promise<ApolloQueryResult<CustomerQuery>>;
  },
  async verifyEmail(email: string) {
    return apolloQuery(verifyEmailQuery, { email }) as Promise<ApolloQueryResult<VerifyEmailQuery>>;
  },
  async removeVirtualCard(input: RemoveVirtualCardInput) {
    return apolloMutation(REMOVE_VIRTUAL_CARD, { input: input }) as Promise<FetchResult<RemoveVirtualCardMutation>>;
  },
  async acknowledgeLeaseOptions(input: AcknowledgeLeaseTermsInput) {
    return apolloMutation(acknowledgeLeaseOptionsMutation, { input: input }) as Promise<FetchResult<AcknowledgeLeaseTermsMutation>>;
  },
  async selectLeaseOption(input: SelectLeaseOptionInput) {
    return apolloMutation(SELECT_LEASE_OPTION, { input: input }) as Promise<ApolloQueryResult<SelectLeaseOptionMutation>>;
  },
  async createDraftApp(input: CreateDraftAppInput) {
    return apolloMutation(CREATE_DRAFT_APP, { input: input }) as Promise<FetchResult<CreateDraftAppMutation>>;
  },
  async getAdditionalCustomerInfo(applicationId: number) {
    return (await apolloQuery(getAdditionalCustomerInfoQuery, { applicationId })) as ApolloQueryResult<GetAdditionalCustomerInfoQuery>;
  },
  async getApplicationItems(applicationId: number) {
    return apolloQuery(getApplicationItemsQuery, { applicationId }) as Promise<ApolloQueryResult<GetLeaseApplicationItemsQuery>>;
  },
  async getExistingLeaseDocuments(orderId: string) {
    return apolloQuery(getExistingLeaseDocumentsQuery, { orderId }) as Promise<ApolloQueryResult<GetExistingLeaseDocumentsQuery>>;
  },
  async getProtectionPlusEnrollmentUrl(input: GetProtectionPlusEnrollmentUrlInput) {
    return apolloMutation(getProtectionPlusEnrollmentUrlMutation, { input }) as Promise<FetchResult<GetProtectionPlusEnrollmentUrlMutation>>;
  },
};

export const apply = gql`
  mutation apply($input: ApplyInput!) {
    apply(input: $input) {
      application {
        loan {
          id
          summary {
            id
            finalPaymentAmount
            paymentAmount
            firstPaymentDue
            annualPercentageRate
            financeCharge
            termLength
            amountFinanced
            loanAmount
            deferredInterestPeriod
          }
          achDetails {
            routingNumber
            accountNumberLast4
          }
          status
          isOtb
          maxLoanAmount
          cartValidationDetails
          options {
            productID
            deferredInterestPeriod
            lineAmount
            merchantDiscountRate
            minimumSpend
            rate
            termLength
          }
        }
        lease {
          id
          status
          declineReason
          decisionCode
          displayId
          publicId
          draftStep
          appStep
          isOtb
          maxLeaseAmount
          cartValidationDetails
          neuroIdIdentityId
          ipUpperBoundary
          ipBehavior
          options {
            leaseTerm
            sacTerm
            earlyBuyoutDiscount
            approvedAmount
            applicationFee
            ipUpperBoundary
            minimumSpend
            authCapture
          }
          employment {
            payPeriod
            lastPayDate
            nextPayDate
            monthlyIncome
            isPaidViaDirectDeposit
          }
          card {
            isExpired
            provider
            lastFour
            expirationDate
          }
          bank {
            accountNumberLast4
            routingNumber
          }
          contractSummaries {
            leaseTerm
            leaseAmount
            cashPlusPrice
            cashPlusTerm
            cashPlusFee
            initialPaymentWithTax
            initialPaymentAmount
            paymentFrequency
            tax
            totalCostOfOwnership
            scheduledPaymentAmounts
          }
        }
        customer {
          id
          firstName
          lastName
          cellPhone
          emailAddress
          birthDate
          tokenizedTaxId
          accommodationAddress {
            line1
            line2
            city
            state
            zip
          }
          billingAddress {
            line1
            line2
            city
            state
            zip
          }
          shippingAddress {
            line1
            line2
            city
            state
            zip
          }
        }
      }
      applicationErrors {
        __typename
        ... on DenialError {
          message
        }
        ... on FatalError {
          message
        }
        ... on BadRequest {
          message
        }
        ... on HardDecline {
          message
          extensions {
            decisionCode
          }
        }
        ... on SoftDecline {
          message
          extensions {
            decisionCode
            declineField
            declineType
            declineReason
            appId
          }
        }
        ... on LeaseRestricted {
          message
          extensions {
            reason
          }
        }
        ... on CartValidation {
          message
          extensions {
            validationErrType
            approvalType
            approvedAmount
            minimumSpend
          }
        }
      }
    }
  }
`;

export const UPDATE_SHIPPING_ADDRESS = gql`
  mutation updateShippingAddress($input: UpdateShippingAddressInput!) {
    updateShippingAddress(input: $input) {
      address {
        line1
        line2
        city
        state
        zip
      }
      updateAddressErrors {
        __typename
        ... on FatalError {
          message
        }
      }
    }
  }
`;

export const generateLeaseContract = gql`
  mutation generateLeaseContract($input: GenerateLeaseContractInput!) {
    generateLeaseContract(input: $input) {
      leaseDocuments {
        welcomeLetter
        leaseAgreement
      }
      generateContractErrors {
        __typename
        ... on FatalError {
          message
          path
        }
        ... on BadRequest {
          message
          path
        }
      }
    }
  }
`;

export const UPDATE_BASIC_INFO_CONTINUED = gql`
  mutation updateBasicInfoContinued($input: UpdateBasicInfoContinuedInput!) {
    updateBasicInfoContinued(input: $input) {
      application {
        loan {
          id
          summary {
            id
            finalPaymentAmount
            paymentAmount
            firstPaymentDue
            annualPercentageRate
            financeCharge
            termLength
            amountFinanced
            loanAmount
            deferredInterestPeriod
          }
          achDetails {
            routingNumber
            accountNumberLast4
          }
          status
          isOtb
          maxLoanAmount
          cartValidationDetails
          options {
            productID
            deferredInterestPeriod
            lineAmount
            merchantDiscountRate
            minimumSpend
            rate
            termLength
          }
        }
        lease {
          id
          status
          declineReason
          decisionCode
          displayId
          publicId
          draftStep
          appStep
          isOtb
          maxLeaseAmount
          cartValidationDetails
          neuroIdIdentityId
          ipUpperBoundary
          ipBehavior
          options {
            leaseTerm
            sacTerm
            earlyBuyoutDiscount
            approvedAmount
            applicationFee
            ipUpperBoundary
            minimumSpend
            authCapture
          }
          employment {
            payPeriod
            lastPayDate
            nextPayDate
            monthlyIncome
            isPaidViaDirectDeposit
          }
          card {
            isExpired
            provider
            lastFour
            expirationDate
          }
          bank {
            accountNumberLast4
            routingNumber
          }
          contractSummaries {
            leaseTerm
            leaseAmount
            cashPlusPrice
            cashPlusTerm
            cashPlusFee
            initialPaymentWithTax
            initialPaymentAmount
            paymentFrequency
            tax
            totalCostOfOwnership
            scheduledPaymentAmounts
          }
        }
        customer {
          id
          firstName
          lastName
          cellPhone
          emailAddress
          birthDate
          tokenizedTaxId
          accommodationAddress {
            line1
            line2
            city
            state
            zip
          }
          billingAddress {
            line1
            line2
            city
            state
            zip
          }
          shippingAddress {
            line1
            line2
            city
            state
            zip
          }
        }
      }
      updateBasicInfoContinuedErrors {
        ... on FatalError {
          message
        }
        ... on BadRequest {
          message
        }
        ... on NotFound {
          message
        }
      }
    }
  }
`;

export const updateEmployment = gql`
  mutation updateEmployment($input: UpdateEmploymentInput!) {
    updateEmployment(input: $input) {
      updateEmploymentErrors {
        __typename
        ... on FatalError {
          message
        }
        ... on BusinessError {
          message
        }
      }
    }
  }
`;

export const UPDATE_DEBIT_DETAILS = gql`
  mutation updateDebitInfo($input: UpdateDebitInfoInput!) {
    updateDebitInfo(input: $input) {
      updateDebitInfoErrors {
        __typename
        ... on FatalError {
          message
        }
        ... on HardDecline {
          message
        }
        ... on SoftDecline {
          message
          extensions {
            declineType
          }
        }
      }
    }
  }
`;

export const AUTHORIZE_CARD = gql`
  mutation authorizeCard($input: AuthorizeCardInput!) {
    authorizeCard(input: $input) {
      application {
        loan {
          id
          summary {
            id
            finalPaymentAmount
            paymentAmount
            firstPaymentDue
            annualPercentageRate
            financeCharge
            termLength
            amountFinanced
            loanAmount
            deferredInterestPeriod
          }
          achDetails {
            routingNumber
            accountNumberLast4
          }
          status
          isOtb
          maxLoanAmount
          cartValidationDetails
          options {
            productID
            deferredInterestPeriod
            lineAmount
            merchantDiscountRate
            minimumSpend
            rate
            termLength
          }
        }
        lease {
          id
          status
          declineReason
          decisionCode
          displayId
          publicId
          draftStep
          appStep
          isOtb
          maxLeaseAmount
          cartValidationDetails
          neuroIdIdentityId
          ipUpperBoundary
          ipBehavior
          options {
            leaseTerm
            sacTerm
            earlyBuyoutDiscount
            approvedAmount
            applicationFee
            ipUpperBoundary
            minimumSpend
            authCapture
          }
          employment {
            payPeriod
            lastPayDate
            nextPayDate
            monthlyIncome
            isPaidViaDirectDeposit
          }
          card {
            isExpired
            provider
            lastFour
            expirationDate
          }
          bank {
            accountNumberLast4
            routingNumber
          }
          contractSummaries {
            leaseTerm
            leaseAmount
            cashPlusPrice
            cashPlusTerm
            cashPlusFee
            initialPaymentWithTax
            initialPaymentAmount
            paymentFrequency
            tax
            totalCostOfOwnership
            scheduledPaymentAmounts
          }
        }
        customer {
          id
          firstName
          lastName
          cellPhone
          emailAddress
          birthDate
          tokenizedTaxId
          accommodationAddress {
            line1
            line2
            city
            state
            zip
          }
          billingAddress {
            line1
            line2
            city
            state
            zip
          }
          shippingAddress {
            line1
            line2
            city
            state
            zip
          }
        }
      }
      authorizeCardErrors {
        __typename
        ... on FatalError {
          message
        }
        ... on HardDecline {
          message
          extensions {
            decisionCode
          }
        }
        ... on SoftDecline {
          message
          extensions {
            decisionCode
          }
        }
      }
    }
  }
`;

export const UPDATE_BANK_ACCOUNT = gql`
  mutation updateBankAccount($input: UpdateBankAccountInput!) {
    updateBankAccount(input: $input) {
      updateBankAccountErrors {
        __typename
        ... on FatalError {
          message
        }
        ... on BadRequest {
          message
        }
        ... on HardDecline {
          message
        }
        ... on SoftDecline {
          message
          extensions {
            declineType
            declineReason
            displayText
          }
        }
        ... on BankConnectionError {
          message
          extensions {
            bankConnectionType
            errCode
            displayText
          }
        }
      }
    }
  }
`;

export const SELECT_LEASE_OPTION = gql`
  mutation selectLeaseOption($input: SelectLeaseOptionInput!) {
    selectLeaseOption(input: $input) {
      selectLeaseOptionErrors {
        __typename
        ... on FatalError {
          message
        }
        ... on NotFound {
          message
        }
      }
      leaseContractSummary {
        leaseTerm
        leaseAmount
        cashPlusPrice
        cashPlusTerm
        cashPlusFee
        initialPaymentWithTax
        initialPaymentAmount
        paymentFrequency
        tax
        totalCostOfOwnership
        scheduledPaymentAmounts
      }
    }
  }
`;

export const SIGN_LEASE_AGREEMENT = gql`
  mutation signLeaseAgreement($input: SignLeaseAgreementInput!) {
    signLeaseAgreement(input: $input) {
      signingErrors {
        __typename
        ... on FatalError {
          message
        }
        ... on BadRequest {
          message
        }
        ... on NotSigned {
          message
        }
      }
    }
  }
`;

export const REMOVE_VIRTUAL_CARD = gql`
  mutation removeVirtualCard($input: RemoveVirtualCardInput!) {
    removeVirtualCard(input: $input) {
      removeVirtualCardErrors {
        __typename
        ... on FatalError {
          message
        }
        ... on BadRequest {
          message
        }
        ... on HasAuthError {
          message
        }
      }
    }
  }
`;

const CREATE_DRAFT_APP = gql`
  mutation createDraftApp($input: CreateDraftAppInput!) {
    createDraftApp(input: $input) {
      leaseApplicationId
      customerId
      createDraftAppErrors {
        __typename
        ... on FatalError {
          message
        }
        ... on BadRequest {
          message
        }
        ... on NotFound {
          message
        }
        ... on LeaseRestricted {
          message
          extensions {
            reason
          }
        }
      }
    }
  }
`;

const acknowledgeLeaseOptionsMutation = gql`
  mutation AcknowledgeLeaseTerms($input: AcknowledgeLeaseTermsInput!) {
    acknowledgeLeaseTerms(input: $input) {
      acknowledgeErrors {
        ... on BadRequest {
          message
          path
        }
        ... on UnauthorizedError {
          message
          path
        }
        ... on FatalError {
          message
          path
        }
        ... on AppNotFound {
          message
          path
        }
      }
    }
  }
`;

const customerSearch = gql`
  query customer($input: CustomerSearchInput!) {
    customer(input: $input) {
      customers {
        id
        firstName
        lastName
        cellPhone
        emailAddress
        tokenizedTaxId
        birthDate
        accommodationAddress {
          line1
          line2
          city
          state
          zip
        }
        billingAddress {
          line1
          line2
          city
          state
          zip
        }
        smsMarketingOptIn
      }
      eligibility {
        applyTypes
        otbProducts {
          loanOptions {
            productID
            deferredInterestPeriod
            lineAmount
            merchantDiscountRate
            minimumSpend
            rate
            termLength
          }
          leaseOptions {
            leaseTerm
            approvedAmount
            applicationFee
            ipUpperBoundary
            earlyBuyoutDiscount
            sacTerm
            minimumSpend
            authCapture
          }
        }
        isEligible
        isLoanEligible
        isLeaseEligible
        eligibilityCode
        otbCode
        application {
          loan {
            id
            summary {
              id
              finalPaymentAmount
              paymentAmount
              firstPaymentDue
              annualPercentageRate
              financeCharge
              termLength
              amountFinanced
              loanAmount
              deferredInterestPeriod
            }
            achDetails {
              routingNumber
              accountNumberLast4
            }
            status
            isOtb
            maxLoanAmount
            cartValidationDetails
            options {
              productID
              deferredInterestPeriod
              lineAmount
              merchantDiscountRate
              minimumSpend
              rate
              termLength
            }
          }
          lease {
            id
            status
            declineReason
            decisionCode
            displayId
            publicId
            draftStep
            appStep
            isOtb
            maxLeaseAmount
            cartValidationDetails
            neuroIdIdentityId
            ipUpperBoundary
            ipBehavior
            options {
              leaseTerm
              sacTerm
              earlyBuyoutDiscount
              approvedAmount
              applicationFee
              ipUpperBoundary
              minimumSpend
              authCapture
            }
            employment {
              payPeriod
              lastPayDate
              nextPayDate
              monthlyIncome
              isPaidViaDirectDeposit
            }
            card {
              isExpired
              provider
              lastFour
              expirationDate
            }
            bank {
              accountNumberLast4
              routingNumber
            }
            contractSummaries {
              leaseTerm
              leaseAmount
              cashPlusPrice
              cashPlusTerm
              cashPlusFee
              initialPaymentWithTax
              initialPaymentAmount
              paymentFrequency
              tax
              totalCostOfOwnership
              scheduledPaymentAmounts
            }
          }
          customer {
            id
            firstName
            lastName
            cellPhone
            emailAddress
            birthDate
            tokenizedTaxId
            accommodationAddress {
              line1
              line2
              city
              state
              zip
            }
            billingAddress {
              line1
              line2
              city
              state
              zip
            }
            shippingAddress {
              line1
              line2
              city
              state
              zip
            }
          }
        }
      }
      customerSearchErrors {
        __typename
      }
    }
  }
`;

const verifyEmailQuery = gql`
  query verifyEmail($email: String!) {
    verifyEmail(email: $email) {
      verifyEmailErrors {
        __typename
        ... on InvalidEmailError {
          message
        }
        ... on FatalError {
          message
        }
        ... on BadRequest {
          message
        }
      }
    }
  }
`;

const getAdditionalCustomerInfoQuery = gql`
  query GetAdditionalCustomerInfo($applicationId: Int!) {
    getAdditionalCustomerInfo(applicationId: $applicationId) {
      fullTaxId
      fullBankAccount
      cardNumberWithBin
    }
  }
`;

const getApplicationItemsQuery = gql`
  query GetLeaseApplicationItems($applicationId: Int!) {
    getLeaseApplicationItems(applicationId: $applicationId) {
      applicationId
      leaseApplicationItemsErrors {
        ... on FatalError {
          message
          path
        }
        ... on NotFound {
          message
          path
        }
        ... on BadRequest {
          message
          path
        }
      }
      items {
        category
        description
        manufacturer
        partNumber
        quantity
        unitPrice
        deliveredQuantity
      }
    }
  }
`;

const getExistingLeaseDocumentsQuery = gql`
  query getExistingLeaseDocuments($orderId: String!) {
    getExistingLeaseDocuments(orderId: $orderId) {
      leaseAgreement
      welcomeLetter
      getExistingLeaseDocumentsErrors {
        ... on FatalError {
          message
          path
        }
        ... on BadRequest {
          message
          path
        }
        ... on UnauthorizedError {
          message
          path
        }
        ... on OrderNotFound {
          message
          path
        }
        ... on AppNotFound {
          message
          path
        }
        ... on NoExistingLeaseContractError {
          message
          path
        }
      }
    }
  }
`;

const getProtectionPlusEnrollmentUrlMutation = gql`
  mutation getProtectionPlusEnrollmentUrl($input: GetProtectionPlusEnrollmentUrlInput!) {
    getProtectionPlusEnrollmentUrl(input: $input) {
      url
      errors {
        __typename
        ... on FatalError {
          message
        }
        ... on BadRequest {
          message
        }
        ... on ProtectionPlusNotApplicable {
          message
        }
      }
    }
  }
`;

export default applicationFormService;
